import React, { useState, useEffect, useRef } from 'react';
import CommentFormWithMentions from './CommentFormWithMentions';
import { Link } from 'react-router-dom';
import { throwTomato, removeTomato, clapComment } from '../services/questionsService';
import { getTomatoCountSync, setTomatoCount, throwTomato as throwTomatoUtil, refundTomato, canThrowTomato, hasTomatoesAvailable } from '../utils/tomatoManagerV2';
import { getClapCountSync, useClap as decrementClap, hasClapsAvailable, canClap, refundClap } from '../utils/clapManagerV2';
import { trackInteraction } from '../services/recommendationService';
import ProfileRing from './ProfileRing';
import { getUserActiveRing } from '../services/profileRingService';

function Comment({ comment, onReply, onDelete, onDeleteReply, currentUser, isAdmin, getCommentBorderColor }) {
  // Check if the commenter is blocked
  const isCommenterBlocked = comment.userIsBlocked || false;
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [borderColor, setBorderColor] = useState('#ddd');
  const commentRef = useRef(null);
  const [tomatoCount, setTomatoCount] = useState(comment.tomatoCount || 0);
  const [tomatoThrown, setTomatoThrown] = useState(false);
  const [throwingTomato, setThrowingTomato] = useState(false);
  const [newTomatoJustThrown, setNewTomatoJustThrown] = useState(false);
  const [showFlyingTomato, setShowFlyingTomato] = useState(false); // State for flying tomato animation
  const [clapCount, setClapCount] = useState(comment.clapCount || 0);
  const [clapGiven, setClapGiven] = useState(false);
  const [givingClap, setGivingClap] = useState(false);
  const [splatLevel, setSplatLevel] = useState(0); // Level of splatter (0-15)
  const [splats, setSplats] = useState([]); // Array to track individual splat positions
  const [userHasTomatoes, setUserHasTomatoes] = useState(true); // Track if user has tomatoes available
  const [userHasClaps, setUserHasClaps] = useState(true); // Track if user has claps available
  const [authorRingId, setAuthorRingId] = useState(null); // Track comment author's active ring
  const [repliesWithRings, setRepliesWithRings] = useState({}); // Track reply authors' rings
  const [showOwnCommentPopup, setShowOwnCommentPopup] = useState(false); // Show emoji popup for own comment interaction
  
  // Function to show the silly popup when user tries to interact with own comment
  const showSillyPopup = () => {
    setShowOwnCommentPopup(true);
    setTimeout(() => {
      setShowOwnCommentPopup(false);
    }, 2000); // Hide after 2 seconds
  };
  
  // Function to render text with @mentions as links
  const renderTextWithMentions = (text) => {
    if (!text) return '';
    
    // Regex to match [@username] format
    const mentionRegex = /\[@(\w+)\]/g;
    
    // Split the text by mentions and render each part
    const parts = text.split(mentionRegex);
    
    if (parts.length === 1) {
      // No mentions, return the original text
      return text;
    }
    
    const matches = text.match(mentionRegex) || [];
    
    // Construct result by alternating between regular text and mentions
    const result = [];
    
    parts.forEach((part, index) => {
      if (part) {
        // Add regular text
        result.push(<span key={`text-${index}`}>{part}</span>);
      }
      
      // Add mention if available
      if (index < matches.length) {
        const username = matches[index].replace(/\[@(\w+)\]/, '$1');
        result.push(
          <Link 
            to={`/profile/${username}`} 
            className="mention"
            key={`mention-${index}`}
          >
            @{username}
          </Link>
        );
      }
    });
    
    return result;
  };
  
  // Get the border color after component mounts
  useEffect(() => {
    if (commentRef.current) {
      const computedStyle = window.getComputedStyle(commentRef.current);
      setBorderColor(computedStyle.borderLeftColor);
    }
  }, []);

  // Load author's active ring
  useEffect(() => {
    const loadAuthorRing = async () => {
      if (comment.userId) {
        try {
          const ringId = await getUserActiveRing(comment.userId);
          setAuthorRingId(ringId);
        } catch (error) {
          console.error('Error loading author ring:', error);
          // No ring on error
          setAuthorRingId(null);
        }
      } else {
        // For comments without userId, no ring
        setAuthorRingId(null);
      }
    };

    loadAuthorRing();
  }, [comment.userId]);

  // Load reply authors' rings
  useEffect(() => {
    const loadReplyRings = async () => {
      if (comment.replies && comment.replies.length > 0) {
        const ringPromises = comment.replies.map(async (reply) => {
          if (reply.userId) {
            try {
              const ringId = await getUserActiveRing(reply.userId);
              return { [reply.userId]: ringId };
            } catch (error) {
              console.error('Error loading reply ring:', error);
              return { [reply.userId]: null };
            }
          }
          return { [reply.userId]: null };
        });

        const ringResults = await Promise.all(ringPromises);
        const ringsMap = ringResults.reduce((acc, ringObj) => ({ ...acc, ...ringObj }), {});
        setRepliesWithRings(ringsMap);
      }
    };

    loadReplyRings();
  }, [comment.replies]);
  
  const handleReply = async (replyText, parentId) => {
    if (replyText && replyText.trim()) {
      const result = await onReply(replyText.trim(), parentId);
      setShowReplyForm(false);
      return result;
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Check if the current user can delete this comment
  const canDelete = () => {
    if (!currentUser) return false;
    // Admin can delete any comment
    if (isAdmin) return true;
    // User can delete their own comment
    return currentUser.uid === comment.userId;
  };
  
  // Debug helper
  useEffect(() => {
    console.log("Comment permissions check:", {
      canDelete: canDelete(),
      currentUser: currentUser?.uid,
      commentUserId: comment.userId,
      isAdmin
    });
  }, [currentUser, comment.userId, isAdmin]);
  
  // Handle delete confirmation
  const handleDeleteClick = () => {
    if (confirmingDelete) {
      // User confirmed deletion
      onDelete(comment.id);
      setConfirmingDelete(false);
    } else {
      // Ask for confirmation
      setConfirmingDelete(true);
    }
  };
  
  // Cancel delete confirmation
  const cancelDelete = () => {
    setConfirmingDelete(false);
  };

  // Function to calculate the splat level based on tomato and clap counts
  const calculateSplatLevel = (tomatoes, claps) => {
    // No tomatoes = no splatter
    if (tomatoes === 0) return 0;

    // IMPORTANT: Always show at least level 1 if there's any tomato
    // Even a single tomato without claps should show the first level of splatter

    // Apply an equation that gives a value between 1-15 based on tomato/clap ratio
    // Tomatoes increase the level, claps decrease it

    // For no claps, base level on tomato count (more tomatoes = higher level)
    let level = claps === 0
      ? Math.min(15, Math.max(1, Math.ceil(tomatoes * 1.5)))
      : 0;

    // When claps exist, use the ratio
    if (claps > 0) {
      // Calculate ratio: tomatoes / (tomatoes + claps)
      const ratio = tomatoes / (tomatoes + claps);
      // Scale the ratio to match our 1-15 levels
      // Force minimum of 1 if there are any tomatoes
      level = Math.max(1, Math.round(ratio * 15));
    }

    // Log for debugging
    console.log(`Splatter calc: ${tomatoes} tomatoes, ${claps} claps = level ${level}`);

    // Ensure level is between 1-15 (not 0) if there are tomatoes
    return Math.max(1, Math.min(15, level));
  };

  // Check if current user has already thrown a tomato or given a clap
  // Generate a random position within the comment area
  const generateRandomSplatPosition = () => {
    // Map of our available splat images by filename
    const availableSplats = [
      { filename: 'splat_0014_1.png', level: 1 },
      { filename: 'splat_0001_splat_0013_2.png', level: 2 },
      { filename: 'splat_0002_splat_0012_3.png', level: 3 },
      { filename: 'splat_0003_splat_0011_4.png', level: 4 },
      { filename: 'splat_0010_5.png', level: 5 },
      { filename: 'splat_0005_splat_0009_6.png', level: 6 },
      { filename: 'splat_0008_splat_0008_7.png', level: 7 },
      { filename: 'splat_0007_8.png', level: 8 },
      { filename: 'splat_0013_splat_0006_9.png', level: 9 },
      { filename: 'splat_0016_splat_0005_10.png', level: 10 },
      { filename: 'splat_0017_splat_0004_11.png', level: 11 },
      { filename: 'splat_0018_splat_0003_12.png', level: 12 },
      { filename: 'splat_0019_splat_0002_13.png', level: 13 },
      { filename: 'splat_0020_splat_0001_14.png', level: 14 },
      { filename: 'splat_0000_15.png', level: 15 }
    ];
    
    // Pick a random splat from available options
    const randomSplat = availableSplats[Math.floor(Math.random() * availableSplats.length)];
    
    return {
      top: Math.random() * 80 + 10, // Keep away from edges (10-90%)
      left: Math.random() * 80 + 10,
      rotation: Math.random() * 360, // Random rotation
      scale: 0.5 + Math.random() * 0.5, // Random size (50-100%)
      opacity: 0.6 + Math.random() * 0.4, // Random opacity (60-100%)
      filename: randomSplat.filename, // Actual filename to use
      level: randomSplat.level, // Level for reference
      id: Date.now() + Math.random() // Unique ID
    };
  };

  // Initialize splats based on existing count or from server
  useEffect(() => {
    const initializeSplats = () => {
      // First check if the comment already has splats saved in its data
      if (comment.splats && Array.isArray(comment.splats) && comment.splats.length > 0 && splats.length === 0) {
        // Use the saved splats from the comment data
        console.log("Loading saved splats from comment data:", comment.splats);
        
        // Make sure each splat has a proper filename defined
        const validSplats = comment.splats.map(splat => {
          if (!splat.filename) {
            // If missing filename, assign a random one
            const randomLevel = Math.floor(Math.random() * 15) + 1;
            return {
              ...splat,
              filename: `splat_0014_1.png`, // Default to level 1
              level: 1
            };
          }
          return splat;
        });
        
        setSplats(validSplats);
      } else if (splats.length === 0) {
        // Also check localStorage for unsaved splats
        try {
          const savedSplats = localStorage.getItem(`splats_${comment.id}`);
          if (savedSplats) {
            const parsedSplats = JSON.parse(savedSplats);
            if (Array.isArray(parsedSplats) && parsedSplats.length > 0) {
              console.log("Loading splats from localStorage backup:", parsedSplats);
              setSplats(parsedSplats);
            }
          }
        } catch (e) {
          console.error("Error loading splats from localStorage:", e);
        }
      } else {
        // If no saved splats, generate based on tomato count
        const initialTomatoCount = comment.tomatoCount || 0;
        if (initialTomatoCount > 0 && splats.length === 0) {
          // Create initial splats for existing tomatoes
          const initialSplats = Array.from({ length: initialTomatoCount }, () => 
            generateRandomSplatPosition()
          );
          setSplats(initialSplats);
          
          // Save the splats to the comment object for persistence
          if (comment) {
            comment.splats = initialSplats;
          }
        }
      }
    };
    
    // Run initialization
    initializeSplats();
  }, []);

  // Monitor tomato availability
  useEffect(() => {
    const checkTomatoAvailability = () => {
      const userId = currentUser?.uid || 'anonymous_user';
      setUserHasTomatoes(hasTomatoesAvailable(userId));
    };
    
    // Check initially
    checkTomatoAvailability();
    
    // Listen for tomato count changes
    const handleTomatoCountChanged = (e) => {
      checkTomatoAvailability();
    };
    
    document.addEventListener('tomatoCountChanged', handleTomatoCountChanged);
    
    return () => {
      document.removeEventListener('tomatoCountChanged', handleTomatoCountChanged);
    };
  }, [currentUser]);

  // Monitor clap availability
  useEffect(() => {
    const checkClapAvailability = () => {
      const userId = currentUser?.uid || 'anonymous_user';
      setUserHasClaps(hasClapsAvailable(userId));
    };
    
    // Check initially
    checkClapAvailability();
    
    // Listen for clap count changes
    const handleClapCountChanged = (e) => {
      checkClapAvailability();
    };
    
    document.addEventListener('clapCountChanged', handleClapCountChanged);
    
    return () => {
      document.removeEventListener('clapCountChanged', handleClapCountChanged);
    };
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      if (comment.tomatoUsers) {
        const hasThrown = comment.tomatoUsers.includes(currentUser.uid);
        console.log(`User ${currentUser.uid} has ${hasThrown ? 'already' : 'not'} thrown a tomato at comment ${comment.id}`);
        setTomatoThrown(hasThrown);
      } else {
        console.log(`No tomatoUsers array for comment ${comment.id}`);
        setTomatoThrown(false);
      }

      if (comment.clapUsers) {
        setClapGiven(comment.clapUsers.includes(currentUser.uid));
      }
    } else {
      console.log('No current user, cannot check if tomato thrown');
    }

    // Update tomato and clap counts from the comment object if they've changed
    if (comment.tomatoCount !== undefined && comment.tomatoCount !== tomatoCount) {
      console.log(`Updating tomato count from ${tomatoCount} to ${comment.tomatoCount}`);
      setTomatoCount(comment.tomatoCount);
    }

    if (comment.clapCount !== undefined && comment.clapCount !== clapCount) {
      setClapCount(comment.clapCount);
    }

    // Calculate the current splat level (still needed for fallback)
    const tomatoes = comment.tomatoCount || tomatoCount || 0;
    const claps = comment.clapCount || clapCount || 0;
    const newSplatLevel = calculateSplatLevel(tomatoes, claps);
    setSplatLevel(newSplatLevel);

    console.log('Tomato/Clap stats:', {
      tomatoes,
      claps,
      tomatoUsers: comment.tomatoUsers ? comment.tomatoUsers.length : 0,
      clapUsers: comment.clapUsers ? comment.clapUsers.length : 0,
      splatLevel: newSplatLevel,
      currentUserHasThrown: currentUser && comment.tomatoUsers ? comment.tomatoUsers.includes(currentUser.uid) : false
    });
  }, [currentUser, comment.tomatoUsers, comment.clapUsers, comment.tomatoCount, comment.clapCount, tomatoCount, clapCount]);

  // Handle clapping for a comment
  const handleClap = async () => {
    if (!currentUser) {
      // Trigger login modal if not logged in
      window.dispatchEvent(new CustomEvent('openLoginModal'));
      return;
    }

    // Don't allow clapping on own comments
    if (currentUser.uid === comment.userId) {
      showSillyPopup();
      return;
    }

    // Don't allow clapping multiple times
    if (clapGiven) {
      console.log('User has already clapped for this comment - no action taken');
      return;
    }

    // Check if user has claps available
    const userId = currentUser?.uid || 'anonymous_user';
    const currentClapCount = getClapCountSync(userId);
    
    if (currentClapCount <= 0) {
      // No claps available
      console.log('Dispatching openClapRefillModal event - user has no claps');
      window.dispatchEvent(new CustomEvent('openClapRefillModal'));
      return;
    }

    // Check if we've clapped at this comment very recently
    if (!canClap(userId, comment.id)) {
      return;
    }

    try {
      setGivingClap(true);

      // Use centralized utility to update clap count
      const newUserClapCount = decrementClap(userId);
      console.log(`Clap use via manager: new count ${newUserClapCount} for user ${userId}`);

      // Update UI optimistically - before the API call
      const newClapCount = clapCount + 1;
      setClapCount(newClapCount);
      setClapGiven(true);

      // Update splat level optimistically - claps reduce the splat level
      const newSplatLevel = calculateSplatLevel(tomatoCount, newClapCount);
      setSplatLevel(newSplatLevel);

      // Animate the clap
      const commentEl = commentRef.current;
      if (commentEl) {
        commentEl.classList.add('clap-given');
        setTimeout(() => {
          commentEl.classList.remove('clap-given');
        }, 500);
      }

      // Call the API function
      const result = await clapComment(comment.id, currentUser.uid);
      
      // Track clap interaction
      if (currentUser) {
        trackInteraction(currentUser.uid, comment.questionId, 'clap', comment.category);
      }

      if (result.success) {
        // Update with the server value if needed
        if (result.clapCount !== undefined && result.clapCount !== newClapCount) {
          setClapCount(result.clapCount);
        }
        
        // Update the comment's clapUsers array locally
        if (!comment.clapUsers) {
          comment.clapUsers = [];
        }
        if (!comment.clapUsers.includes(currentUser.uid)) {
          comment.clapUsers.push(currentUser.uid);
        }
      } else {
        // API rejected the clap (user already clapped)
        console.error('Server rejected clap:', result.message);
        
        // Revert the optimistic updates
        setClapGiven(false);
        setClapCount(clapCount);
        
        // Refund the clap to the user
        const refundedCount = refundClap(userId);
        console.log(`Clap refunded due to server rejection: new count ${refundedCount}`);
        
        if (result.message) {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error('Error clapping for comment:', error);
      // Revert optimistic update
      setClapCount(clapCount);
      setClapGiven(false);
      
      // Refund the clap on error
      const refundedCount = refundClap(userId);
      console.log(`Clap refunded due to error: new count ${refundedCount}`);
      
      alert('Failed to clap for comment. Please try again.');
    } finally {
      setGivingClap(false);
    }
  };

  // Handle toggling a tomato (throw or remove) at a comment
  const handleToggleTomato = async () => {
    if (!currentUser) {
      // Trigger login modal if not logged in
      window.dispatchEvent(new CustomEvent('openLoginModal'));
      return;
    }

    // Don't allow throwing tomatoes at own comments
    if (currentUser.uid === comment.userId) {
      showSillyPopup();
      return;
    }
    
    // Check if we're already in a throwing operation
    if (throwingTomato) {
      console.log("Already in a tomato operation, please wait");
      return;
    }
    
    // If user previously threw a tomato at this comment
    if (tomatoThrown) {
      // Don't allow removing tomatoes - once thrown, they stay thrown
      console.log("User already threw a tomato at this comment - no action taken");
      return;
    }
    
    // For a new throw, check tomato availability using the centralized utility
    try {
      const userId = currentUser?.uid || 'anonymous_user';
      
      // Get current tomato count from the centralized manager
      const currentCount = getTomatoCountSync(userId);
      
      if (currentCount <= 0) {
        // No tomatoes available
        console.log('Dispatching openTomatoRefillModal event - user has no tomatoes');
        window.dispatchEvent(new CustomEvent('openTomatoRefillModal'));
        return;
      }
      
      // Check if we've thrown a tomato at this comment very recently
      // This prevents accidental double-clicks from counting twice
      if (!canThrowTomato(userId, comment.id)) {
        return;
      }
      
      // Has tomatoes and enough time has passed, proceed with throw
      completeToggleTomato(false);
    } catch (e) {
      console.error("Error checking tomato availability:", e);
      // Default to allowing the throw
      completeToggleTomato(false);
    }
  };

  // Handle the response from the tomato availability check
  const handleTomatoAvailabilityResponse = (hasTomatoes) => {
    if (hasTomatoes) {
      // User has tomatoes available, proceed with the tomato throw
      completeToggleTomato(false);
    }
    // If no tomatoes, the Home component will handle showing the refill modal
  };

  // Complete the tomato toggle operation (throw or remove)
  const completeToggleTomato = async (isRemoving) => {
    // Log current state for debugging
    console.log("Toggle tomato - current state:", {
      tomatoThrown,
      tomatoCount,
      currentUser: currentUser?.uid,
      commentId: comment.id,
      tomatoUsers: comment.tomatoUsers || [],
      isRemoving
    });

    try {
      setThrowingTomato(true);

      if (isRemoving) {
        console.log("Removing tomato (untomato)");
        // REMOVING a tomato (untomato)
        
        // IMPORTANT: Don't use the event system for this, it causes glitches
        // Update the UI first - mark as not thrown
        setTomatoThrown(false);
        
        // Use centralized utility to update tomato count and avoid glitches
        try {
          const userId = currentUser?.uid || 'anonymous_user';
          
          // Use the refundTomato utility function
          const newCount = refundTomato(userId);
          console.log(`Tomato refund via manager: new count ${newCount} for user ${userId}`);
          
          // Also dispatch event to keep Home.js in sync
          // The directCount ensures all components use the same exact number
          // This event complements the tomatoManager's own events for backward compatibility
          window.dispatchEvent(new CustomEvent('refundTomato', {
            detail: { userId: userId, directCount: newCount }
          }));
        } catch (e) {
          console.error("Error refunding tomato:", e);
        }

        // Update splat level optimistically
        // Calculate what the new tomato count will be
        const estimatedTomatoCount = Math.max(0, tomatoCount - 1);
        const newSplatLevel = calculateSplatLevel(estimatedTomatoCount, clapCount);
        setSplatLevel(newSplatLevel);
        
        // DON'T remove splats when removing a tomato
        // We'll keep all the splats in place even when reducing count
        // This way the visual state remains consistent

        // Call the API function to remove tomato
        const result = await removeTomato(comment.id, currentUser.uid);
        console.log("Remove tomato API result:", result);

        if (result.success) {
          // Update with the server value if needed
          if (result.tomatoCount !== undefined) {
            setTomatoCount(result.tomatoCount);
            // Recalculate splat level again if server value differs
            setSplatLevel(calculateSplatLevel(result.tomatoCount, clapCount));
            
            // Make sure splats array matches tomato count
            if (result.tomatoCount < splats.length) {
              setSplats(prevSplats => prevSplats.slice(0, result.tomatoCount));
            }
          }

          // We already triggered the refund event at the beginning, 
          // don't trigger it again to avoid double counting
          console.log("Tomato was already refunded");
        }
      } else {
        console.log("Throwing a tomato");
        // THROWING a tomato
        // IMPORTANT: Don't use the event system for this, it causes glitches
        // Update the UI first - mark as thrown
        setTomatoThrown(true);
        
        // Use centralized utility to update tomato count and avoid glitches
        try {
          const userId = currentUser?.uid || 'anonymous_user';
          
          // The timestamp check is already handled by canThrowTomato() call earlier
          
          // Use the throwTomato utility function (it's async!)
          const newCount = await throwTomatoUtil(userId);
          console.log(`Tomato throw via manager: new count ${newCount} for user ${userId}`);
          
          // Also dispatch event to keep Home.js in sync
          // The directCount ensures all components use the same exact number
          // This event complements the tomatoManager's own events for backward compatibility
          window.dispatchEvent(new CustomEvent('useTomato', {
            detail: { userId: userId, directCount: newCount }
          }));
          
          // If this was the last tomato, show refill modal
          if (newCount === 0) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openTomatoRefillModal'));
            }, 300);
          }
        } catch (e) {
          console.error("Error updating tomato count:", e);
        }

        // Add a new splat with random position
        const newSplat = generateRandomSplatPosition();
        const updatedSplats = [...splats, newSplat];
        setSplats(updatedSplats);
          
        // Save the updated splats to the comment data
        comment.splats = updatedSplats;
        
        // Track tomato interaction
        if (currentUser) {
          trackInteraction(currentUser.uid, comment.questionId, 'tomato', comment.category);
        }

        // Update splat level optimistically (still needed for fallback)
        // Calculate based on current tomato count + 1 (since we're adding a tomato)
        const estimatedTomatoCount = tomatoCount + 1;
        const newSplatLevel = calculateSplatLevel(estimatedTomatoCount, clapCount);
        setSplatLevel(newSplatLevel);

        // Set the flag to indicate a new tomato was just thrown - for animation
        setNewTomatoJustThrown(true);

        // Trigger the flying tomato animation
        setShowFlyingTomato(true);
        console.log("Animation flags set:", { newTomatoJustThrown: true, showFlyingTomato: true });

        // Clear the flags after appropriate delays
        setTimeout(() => {
          setNewTomatoJustThrown(false);
          console.log("Cleared newTomatoJustThrown flag");
        }, 600); // Slightly longer than the splash animation duration

        setTimeout(() => {
          setShowFlyingTomato(false);
          console.log("Cleared showFlyingTomato flag");
        }, 1000); // Duration of the flying tomato animation

        // Animate the tomato throw
        const commentEl = commentRef.current;
        if (commentEl) {
          commentEl.classList.add('tomato-hit');
          setTimeout(() => {
            commentEl.classList.remove('tomato-hit');
          }, 500);
        }

        // Store splats in localStorage before API call to preserve them
        localStorage.setItem(`splats_${comment.id}`, JSON.stringify(updatedSplats));

        // Call the API function
        const result = await throwTomato(comment.id, currentUser.uid);
        console.log("Throw tomato API result:", result);

        if (result.success) {
          // Update with the server value if needed
          if (result.tomatoCount !== undefined) {
            setTomatoCount(result.tomatoCount);
          }

          // Update the comment's tomatoUsers array locally
          if (!comment.tomatoUsers) {
            comment.tomatoUsers = [];
          }
          if (!comment.tomatoUsers.includes(currentUser.uid)) {
            comment.tomatoUsers.push(currentUser.uid);
          }

          // Clean up temporary splat storage since API succeeded
          localStorage.removeItem(`splats_${comment.id}`);
          
          console.log("Tomato thrown successfully!");
        } else {
          // API failed or rejected the throw
          console.error('Server rejected tomato throw:', result.message || 'Unknown error');
          
          // Revert the optimistic updates
          setTomatoThrown(false);
          setSplats(splats.slice(0, -1)); // Remove the last splat
          
          // Refund the tomato to the user
          const refundedCount = refundTomato(currentUser.uid);
          console.log(`Tomato refunded due to server rejection: new count ${refundedCount}`);
          
          // Clean up temporary splat storage since we're reverting
          localStorage.removeItem(`splats_${comment.id}`);
          
          if (result.message) {
            alert(result.message);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling tomato:', error);
      // Revert optimistic update
      if (isRemoving) {
        // We were trying to remove, so revert back to thrown
        setTomatoThrown(true);
        setTomatoCount(tomatoCount); // Original count
      } else {
        // We were trying to throw, so revert back to not thrown
        setTomatoThrown(false);
        setTomatoCount(tomatoCount); // Original count
      }
      
      // Clear animation states on error
      setShowFlyingTomato(false);
      setNewTomatoJustThrown(false);
      
      alert('Failed to toggle tomato. Please try again.');
    } finally {
      setThrowingTomato(false);
    }
  };

  return (
    <div
      className="comment"
      ref={commentRef}
      style={{
        border: `2px solid ${getCommentBorderColor(comment.userId)}`,
        backgroundColor: isCommenterBlocked ? '#fafafa' : 'white',
        borderRadius: '18px',
        boxShadow: `0 4px 15px rgba(0, 0, 0, 0.08)`,
        // For white borders, add a subtle outline so they're visible against white background
        outline: getCommentBorderColor(comment.userId) === 'white' ? '1px solid rgba(0,0,0,0.1)' : 'none',
        position: 'relative'
      }}>
      
      {/* Silly popup for own comment interactions */}
      {showOwnCommentPopup && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '3rem',
            zIndex: 1000,
            animation: 'sillyPopupFade 2s ease-out forwards',
            pointerEvents: 'none'
          }}
        >
          🤪🪿
        </div>
      )}
      <div className="comment-header">
        <div className="comment-author-container">
          <div className="comment-avatar">
            <ProfileRing ringId={authorRingId} size="small">
              {comment.userPhotoURL ? (
                <img src={comment.userPhotoURL} alt={comment.userName || 'User'} />
              ) : (
                <span className="comment-avatar-letter">
                  {(comment.userName || 'A')[0]}
                </span>
              )}
            </ProfileRing>
          </div>
          <div className="comment-author-info">
            <div className="comment-author">
              <Link to={`/profile/${comment.userId}`} className="comment-author-link">
                {comment.userName || 'User'}
              </Link>
              {isCommenterBlocked && (
                <span style={{ 
                  marginLeft: '5px', 
                  backgroundColor: '#fff8e1', 
                  color: '#ff6f00', 
                  fontSize: '10px',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  fontWeight: 'bold'
                }}>
                  BLOCKED
                </span>
              )}
            </div>
            <span className="comment-date">{formatDate(comment.createdAt)}</span>
          </div>
        </div>
        <div className="comment-actions">
          {canDelete() && (
            <div className="dropdown">
              <button className="ellipsis-button">⋮</button>
              <div className="dropdown-content">
                {confirmingDelete ? (
                  <>
                    <button 
                      className="dropdown-item confirm" 
                      onClick={handleDeleteClick}
                    >
                      Confirm Delete
                    </button>
                    <button 
                      className="dropdown-item cancel" 
                      onClick={cancelDelete}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button 
                    className="dropdown-item delete" 
                    onClick={handleDeleteClick}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Multiple tomato splatter images */}
      <div className="tomato-splatter-container" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        zIndex: 1,
        pointerEvents: 'none'
      }}>
        {/* Map through the splats array to render individual splats */}
        {splats.map((splat) => (
          <img
            key={splat.id}
            src={`/${splat.filename || `splat_0014_1.png`}`}
            className={`tomato-splatter ${newTomatoJustThrown && splat.id === splats[splats.length - 1]?.id ? 'new-splat' : ''}`}
            alt=""
            onError={(e) => {
              console.error(`Failed to load image: ${e.target.src}`);
              // Try to load level 1 as fallback
              e.target.src = `/splat_0014_1.png`;
            }}
            style={{
              position: 'absolute',
              top: `${splat.top}%`,
              left: `${splat.left}%`,
              width: '50%', // Base size that gets scaled
              height: '50%',
              transform: `translate(-50%, -50%) rotate(${splat.rotation}deg) scale(${splat.scale})`,
              opacity: splat.opacity,
              mixBlendMode: 'multiply',
              filter: 'contrast(1.2) saturate(1.2)',
              transition: 'all 0.5s ease',
              zIndex: 1 // Place behind the text
            }}
          />
        ))}
        
        {/* Fallback for when no splats array exists yet */}
        {tomatoCount > 0 && splats.length === 0 && (
          <img
            src={`/splat_${String(15 - splatLevel).padStart(4, '0')}_${splatLevel}.png`}
            className={`tomato-splatter ${newTomatoJustThrown ? 'new-splat' : ''}`}
            alt=""
            onError={(e) => {
              console.error(`Failed to load image: ${e.target.src}`);
              // Try to load level 1 as fallback
              e.target.src = `/splat_0014_1.png`;
            }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '80%',
              height: '80%',
              transform: 'translate(-50%, -50%)',
              objectFit: 'contain',
              opacity: 0.8,
              mixBlendMode: 'multiply',
              filter: 'contrast(1.2) saturate(1.2)',
              transition: 'all 0.5s ease',
              zIndex: 1
            }}
          />
        )}
      </div>

      <div className="comment-text" style={{
        ...isCommenterBlocked ? { opacity: 0.7 } : {},
        position: 'relative',
        zIndex: 5,
        backgroundColor: 'rgba(255, 255, 255, 0.9)', // More opaque background for better text visibility
        padding: '8px',
        borderRadius: '8px',
        color: '#333', // Ensure text is dark enough to be visible on light background
        fontSize: '0.95rem',
        lineHeight: '1.4'
      }}>
        {renderTextWithMentions(comment.text)}
      </div>
      <div className="comment-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className="reply-button"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            {showReplyForm ? 'Cancel' : 'Reply'}
          </button>

          {/* Tomato button */}
          <div style={{ position: 'relative' }}>
            <button
              className={`tomato-button ${tomatoThrown ? 'tomato-thrown' : ''} ${!userHasTomatoes && !tomatoThrown ? 'disabled' : ''}`}
              onClick={handleToggleTomato}
              disabled={throwingTomato || (!userHasTomatoes && !tomatoThrown)}
              title={tomatoThrown ? "You threw a tomato!" : (!userHasTomatoes ? "No tomatoes left! Click to get more" : "Throw a tomato")}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: tomatoThrown ? 'default' : (!userHasTomatoes && !tomatoThrown) ? 'not-allowed' : 'pointer',
                fontSize: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                opacity: tomatoThrown ? 1 : (!userHasTomatoes ? 0.3 : 1),
                color: '#e53935',
                position: 'relative',
                padding: '4px',
                filter: (!userHasTomatoes && !tomatoThrown) ? 'grayscale(100%)' : 'none'
              }}
            >
              🍅
              {tomatoCount > 0 && (
                <span style={{
                  fontSize: '0.8rem',
                  marginLeft: '3px',
                  fontWeight: 'bold',
                  color: '#e53935'
                }}>
                  {tomatoCount}
                </span>
              )}
            </button>

            {/* Flying tomato animation */}
            {showFlyingTomato && (
              <img
                src="/img/thrown.gif"
                alt="Flying tomato"
                className="flying-tomato"
                style={{
                  top: '0px',
                  right: '20px',
                  width: '40px',
                  height: '40px'
                }}
                onError={(e) => {
                  console.warn('Failed to load tomato gif');
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>

          {/* Clap button */}
          <button
            className={`clap-button ${clapGiven ? 'clap-given' : ''} ${!userHasClaps && !clapGiven ? 'disabled' : ''}`}
            onClick={handleClap}
            disabled={givingClap || (!userHasClaps && !clapGiven)}
            title={clapGiven ? "You clapped!" : (!userHasClaps ? "No claps left! Click to get more" : "Clap for this comment")}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: clapGiven ? 'default' : (!userHasClaps && !clapGiven) ? 'not-allowed' : 'pointer',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              opacity: clapGiven ? 1 : (!userHasClaps ? 0.3 : 1),
              color: '#4CAF50',
              filter: (!userHasClaps && !clapGiven) ? 'grayscale(100%)' : 'none'
            }}
          >
            👏
            {clapCount > 0 && (
              <span style={{
                fontSize: '0.8rem',
                marginLeft: '3px',
                fontWeight: 'bold',
                color: '#4CAF50'
              }}>
                {clapCount}
              </span>
            )}
          </button>
        </div>
      </div>
      
      {showReplyForm && (
        <div className="reply-form">
          <CommentFormWithMentions
            onSubmit={handleReply}
            parentId={comment.id}
            onCancel={() => setShowReplyForm(false)}
            userVoteOption={getCommentBorderColor(comment.userId) === 'white' ? 'A' : 'B'}
            questionId={comment.questionId}
            contextType="reply"
          />
        </div>
      )}
      
      {comment.replies && comment.replies.length > 0 && (
        <div className="replies">
          {comment.replies.map(reply => (
            <div
              key={reply.id}
              id={`reply-${reply.id}`}
              className="reply"
              style={{
                border: `2px solid ${getCommentBorderColor(reply.userId)}`,
                borderRadius: '14px',
                boxShadow: `0 3px 10px rgba(0, 0, 0, 0.06)`,
                // For white borders, add a subtle outline so they're visible against white background
                outline: getCommentBorderColor(reply.userId) === 'white' ? '1px solid rgba(0,0,0,0.1)' : 'none',
                position: 'relative' // Added for absolute positioning of flying tomato
              }}
            >
              <div className="reply-header">
                <div className="reply-author-container">
                  <div className="reply-avatar">
                    <ProfileRing ringId={repliesWithRings[reply.userId]} size="small">
                      {reply.userPhotoURL ? (
                        <img src={reply.userPhotoURL} alt={reply.userName || 'User'} />
                      ) : (
                        <span className="reply-avatar-letter">
                          {(reply.userName || 'A')[0]}
                        </span>
                      )}
                    </ProfileRing>
                  </div>
                  <div className="reply-author-info">
                    <span className="reply-author">
                      <Link to={`/profile/${reply.userId}`} className="comment-author-link">
                        {reply.userName || 'User'}
                      </Link>
                      {reply.userIsBlocked && (
                        <span style={{ 
                          marginLeft: '5px', 
                          backgroundColor: '#fff8e1', 
                          color: '#ff6f00', 
                          fontSize: '10px',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          fontWeight: 'bold'
                        }}>
                          BLOCKED
                        </span>
                      )}
                    </span>
                    <span className="reply-date">{formatDate(reply.createdAt)}</span>
                  </div>
                </div>
                
                {/* Reply Delete Button */}
                {(isAdmin || (currentUser && currentUser.uid === reply.userId)) && (
                  <div className="dropdown small">
                    <button className="ellipsis-button small">⋮</button>
                    <div className="dropdown-content right">
                      <button 
                        className="dropdown-item delete" 
                        onClick={() => onDeleteReply(reply.id, comment.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Single tomato splatter image for replies based on level */}
              <div className="tomato-splatter-container reply-container" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                borderRadius: '14px',
                overflow: 'hidden',
                zIndex: 1,
                pointerEvents: 'none'
              }}>
                {/* Calculate splat level for replies */}
                {(() => {
                  // Only show if there are tomatoes
                  if (!reply.tomatoCount || reply.tomatoCount === 0) return null;

                  // Calculate splat level for this reply
                  const replySplatLevel = calculateSplatLevel(reply.tomatoCount, reply.clapCount);

                  // Track if this is a newly thrown tomato
                  const justThrown = currentUser && !reply.tomatoUsers?.includes(currentUser.uid);

                  return reply.tomatoCount > 0 && (
                    <img
                      key={`reply-splatter-${reply.id}`}
                      src={`${process.env.PUBLIC_URL}/splat_${String(15 - replySplatLevel).padStart(4, '0')}_${replySplatLevel}.png`}
                      className={`tomato-splatter reply-splatter ${justThrown ? 'new-splat' : ''}`}
                      alt=""
                      onError={(e) => {
                        console.error(`Failed to load reply image: ${e.target.src}`);
                        // Try to load level 1 as fallback
                        e.target.src = `${process.env.PUBLIC_URL}/splat_0014_1.png`;
                      }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '75%', // Slightly smaller for replies
                        height: '75%',
                        transform: 'translate(-50%, -50%) rotate(180deg)', // Center and rotate
                        objectFit: 'contain', // Keep original proportions
                        opacity: 0.8, // Reduce opacity for better text visibility
                        mixBlendMode: 'multiply',
                        filter: 'contrast(1.2) saturate(1.2)',
                        transition: 'all 0.5s ease',
                        // Rotation is now handled in the transform: translate line
                        zIndex: 1 // Place behind the text
                      }}
                    />
                  );
                })()}
              </div>
              
              <div className="reply-content" style={{
                ...reply.userIsBlocked ? { opacity: 0.7 } : {},
                position: 'relative',
                zIndex: 5,
                backgroundColor: 'rgba(255, 255, 255, 0.9)', // More opaque background for better text visibility
                padding: '8px',
                borderRadius: '8px',
                color: '#333', // Ensure text is dark enough to be visible on light background
                fontSize: '0.9rem',
                lineHeight: '1.4'
              }}>
                {renderTextWithMentions(reply.text)}
              </div>
              <div className="reply-footer" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '0.5rem' }}>
                {/* Tomato button for replies */}
                {currentUser && currentUser.uid !== reply.userId && (
                  <div style={{ position: 'relative' }}>
                    <button
                      className={`tomato-button small ${reply.tomatoUsers && reply.tomatoUsers.includes(currentUser?.uid) ? 'tomato-thrown' : ''}`}
                      onClick={() => {
                        if (!currentUser) {
                          window.dispatchEvent(new CustomEvent('openLoginModal'));
                          return;
                        }

                        // Don't allow throwing tomatoes at own replies
                        if (currentUser.uid === reply.userId) {
                          showSillyPopup();
                          return;
                        }

                        // Check if user has already thrown a tomato
                        const hasThrown = reply.tomatoUsers && reply.tomatoUsers.includes(currentUser.uid);

                        try {
                          if (hasThrown) {
                            // REMOVE the tomato (untomato)
                            console.log("Removing tomato from reply:", reply.id);
                            
                            // Use centralized utility to update tomato count and avoid glitches
                            const userId = currentUser?.uid || 'anonymous_user';
                            
                            // Use the refundTomato utility function
                            const newCount = refundTomato(userId);
                            console.log(`Tomato refund via manager (reply): new count ${newCount} for user ${userId}`);
                            
                            // Also dispatch event to keep Home.js in sync
                            // The directCount ensures all components use the same exact number
                            // This event complements the tomatoManager's own events for backward compatibility
                            window.dispatchEvent(new CustomEvent('refundTomato', {
                              detail: { userId, directCount: newCount }
                            }));
                            
                            // Optimistically update UI
                            const replyEl = document.getElementById(`reply-${reply.id}`);
                            if (replyEl) {
                              // Update local reply state (tomato count & users)
                              if (reply.tomatoUsers) {
                                reply.tomatoUsers = reply.tomatoUsers.filter(uid => uid !== currentUser.uid);
                              }
                              reply.tomatoCount = Math.max(0, (reply.tomatoCount || 0) - 1);
                              
                              // Update UI elements
                              const countEl = replyEl.querySelector('.tomato-count');
                              if (countEl) {
                                countEl.textContent = reply.tomatoCount;
                              }
                              
                              // Update splatter if needed
                              const replySplatter = replyEl.querySelector('.reply-splatter');
                              if (replySplatter) {
                                // Always show splatter, even when tomato count is 0
                                // Just update the level based on the count
                                const newSplatLevel = calculateSplatLevel(Math.max(1, reply.tomatoCount), reply.clapCount || 0);
                                replySplatter.src = `${process.env.PUBLIC_URL}/splat_${String(15 - newSplatLevel).padStart(4, '0')}_${newSplatLevel}.png`;
                              }
                              
                              // Update button state
                              const buttonEl = replyEl.querySelector('.tomato-button');
                              if (buttonEl) {
                                buttonEl.classList.remove('tomato-thrown');
                              }
                            }
                            
                            // Call the server API in the background
                            removeTomato(reply.id, currentUser.uid)
                              .catch(error => {
                                console.error('Error removing tomato from reply:', error);
                              });
                          } else {
                            // THROWING a tomato
                            console.log("Throwing tomato at reply:", reply.id);
                            
                            // Use centralized utility to check tomato availability
                            const userId = currentUser?.uid || 'anonymous_user';
                            
                            // Get current tomato count from the centralized manager
                            const currentCount = getTomatoCountSync(userId);
                            
                            if (currentCount <= 0) {
                              // No tomatoes available, show refill modal
                              window.dispatchEvent(new CustomEvent('openTomatoRefillModal'));
                              return;
                            }
                            
                            // Check for recent throws (prevent double-throws)
                            if (!canThrowTomato(userId, reply.id)) {
                              return;
                            }
                            
                            // Use the throwTomato utility function
                            const newTomatoCount = throwTomatoUtil(userId);
                            console.log(`Tomato throw via manager (reply): new count ${newTomatoCount} for user ${userId}`);
                            
                            // Also dispatch event to keep Home.js in sync
                            // The directCount ensures all components use the same exact number
                            // This event complements the tomatoManager's own events for backward compatibility
                            window.dispatchEvent(new CustomEvent('useTomato', {
                              detail: { userId, directCount: newTomatoCount }
                            }));
                            
                            // If this was the last tomato, show refill modal
                            if (newTomatoCount === 0) {
                              setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('openTomatoRefillModal'));
                              }, 300);
                            }
                            
                            // Show flying tomato animation
                            const replyEl = document.getElementById(`reply-${reply.id}`);
                            if (replyEl) {
                              const flyingTomato = document.createElement('img');
                              flyingTomato.src = '/img/thrown.gif';
                              flyingTomato.className = 'flying-tomato';
                              flyingTomato.alt = '';
                              flyingTomato.style.top = '0px';
                              flyingTomato.style.right = '20px';
                              flyingTomato.style.width = '40px';
                              flyingTomato.style.height = '40px';
                              flyingTomato.style.zIndex = '100';
                              flyingTomato.style.position = 'absolute';
                              replyEl.appendChild(flyingTomato);
                              
                              // Remove animation after it completes
                              setTimeout(() => {
                                flyingTomato.remove();
                                console.log('Removed flying tomato animation from reply');
                              }, 1000);
                              
                              // Update local reply state (tomato count & users)
                              if (!reply.tomatoUsers) {
                                reply.tomatoUsers = [currentUser.uid];
                              } else if (!reply.tomatoUsers.includes(currentUser.uid)) {
                                reply.tomatoUsers.push(currentUser.uid);
                              }
                              reply.tomatoCount = (reply.tomatoCount || 0) + 1;
                              
                              // Update UI elements
                              const countEl = replyEl.querySelector('.tomato-count');
                              if (countEl) {
                                countEl.textContent = reply.tomatoCount;
                              }
                              
                              // Update button state
                              const buttonEl = replyEl.querySelector('.tomato-button');
                              if (buttonEl) {
                                buttonEl.classList.add('tomato-thrown');
                              }
                            }
                            
                            // Call the server API in the background
                            throwTomato(reply.id, currentUser.uid)
                              .catch(error => {
                                console.error('Error throwing tomato at reply:', error);
                              });
                          }
                        } catch (error) {
                          console.error('Error handling tomato action for reply:', error);
                        }
                      }}
                      title={reply.tomatoUsers && reply.tomatoUsers.includes(currentUser?.uid)
                        ? "Click to remove your tomato"
                        : "Throw a tomato"}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        opacity: reply.tomatoUsers && reply.tomatoUsers.includes(currentUser?.uid) ? 0.7 : 1,
                        color: '#e53935',
                        position: 'relative',
                        padding: '3px'
                      }}
                    >
                      🍅
                      {reply.tomatoCount > 0 && (
                        <span
                          className="tomato-count"
                          style={{
                            fontSize: '0.7rem',
                            marginLeft: '2px',
                            fontWeight: 'bold',
                            color: '#e53935'
                          }}
                        >
                          {reply.tomatoCount}
                        </span>
                      )}
                    </button>
                  </div>
                )}
                
                {/* Clap button for replies */}
                {currentUser && (
                  <button
                    className="clap-button small"
                    onClick={() => {
                      if (!currentUser) {
                        window.dispatchEvent(new CustomEvent('openLoginModal'));
                        return;
                      }
                      
                      if (!reply.clapUsers || !reply.clapUsers.includes(currentUser.uid)) {
                        clapComment(reply.id, currentUser.uid);
                      }
                    }}
                    disabled={reply.clapUsers && reply.clapUsers.includes(currentUser?.uid)}
                    title={reply.clapUsers && reply.clapUsers.includes(currentUser?.uid) ? 
                      "You've already clapped for this comment" : "Clap for this comment"}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: reply.clapUsers && reply.clapUsers.includes(currentUser?.uid) ? 'default' : 'pointer',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: reply.clapUsers && reply.clapUsers.includes(currentUser?.uid) ? 0.7 : 1,
                      color: '#4CAF50'
                    }}
                  >
                    👏
                    {reply.clapCount > 0 && (
                      <span style={{
                        fontSize: '0.7rem',
                        marginLeft: '2px',
                        fontWeight: 'bold',
                        color: '#4CAF50'
                      }}>
                        {reply.clapCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Comment;